import { deriveIsAssetUploaded } from "./drawing2dProcessingStatus";

/**
 * capture_at を日付と時刻に分ける（パースせず文字列のまま扱う）。
 * ISO8601（"T" 区切り）と "YYYY-MM-DD HH:mm"（空白区切り）の両方に対応する。
 *
 * @param {string} captureAt
 * @returns {{ date: string, time: string }}
 */
export const splitCaptureAt = (captureAt) => {
	if (typeof captureAt !== "string" || captureAt.length === 0) {
		return { date: "", time: "" };
	}
	if (captureAt.includes("T")) {
		const [date, rawTime = ""] = captureAt.split("T");
		return { date, time: rawTime.replace(/(\.\d+)?Z?$/, "") };
	}
	if (captureAt.includes(" ")) {
		const [date, time = ""] = captureAt.split(" ");
		return { date, time };
	}
	return { date: captureAt, time: "" };
};

// terrain, editable, constructable,
export class Drawing2dImage {
	constructor({
		name,
		file,
		fileName,
		isAssetUploaded,
		assetId,
		centerLatitude,
		centerLongitude,
		rotateDegree,
		imageWidth,
		imageHeight,
		transparency = 0,
		isDraped = false,
		isVisible = true,
		selectedDate = undefined,
		selectedTime = undefined,
		expansion = false,
		tilingProgress = false, // will delete
		processingStatus = "complete", // will delete
		elevation = Number.NaN,
		isLoaded = false,
		hasImage = false,
	} = {}) {
		this.name = name;
		this.file = file;
		this.fileName = fileName;
		this.isAssetUploaded = isAssetUploaded;
		this.assetId = assetId;
		this.latitude = centerLatitude;
		this.longitude = centerLongitude;
		this.rotateDegree = rotateDegree;
		this.imageWidth = imageWidth;
		this.imageHeight = imageHeight;
		this.transparency = transparency;
		this.isVisible = isVisible;
		this.selectedDate = selectedDate;
		this.selectedTime = selectedTime;
		this.isDraped = isDraped;
		this.expansion = expansion;
		this.tilingProgress = tilingProgress;
		this.processingStatus = processingStatus;
		this.is_creating = false;
		this.isLoaded = isLoaded;
		this.elevation = elevation;
		this.hasImage = hasImage;
	}

	getProcessingStatus() {
		return this.processingStatus;
	}

	setProcessingStatus(value) {
		this.processingStatus = value;
	}

	/**
	 * 一覧 API の 1 行でサーバ由来のフィールドを更新する。
	 * isVisible / transparency / isLoaded / expansion などの UI 状態は保持する
	 * （PointCloud.update と同じ規約）。
	 *
	 * @param {import("./drawing2dProcessingStatus").Drawing2dImageListRow} row
	 */
	update(row) {
		const { date, time } = splitCaptureAt(row.capture_at);
		this.name = row.drawing_name;
		this.fileName = row.image_name;
		this.selectedDate = date;
		this.selectedTime = time;
		// null は従来の画像アップロード行として完了扱いにする
		this.processingStatus = row.processing_status ?? "complete";
		// 画像なしで登録した図面は legacy server（registerDrawing2dNoImage）が drawing_path に "" を書き込む
		this.hasImage =
			typeof row.drawing_path === "string" && row.drawing_path.length > 0;
		this.isAssetUploaded = deriveIsAssetUploaded(row.processing_status ?? null);
	}

	setIsAssetUploaded(value) {
		this.isAssetUploaded = value;
	}

	getName() {
		return this.name;
	}

	getType() {
		return "reference-topology";
	}

	getExtension() {
		const lastDotIndex = this.fileName.lastIndexOf(".");
		const extension = this.fileName.substring(lastDotIndex + 1);
		return extension;
	}

	getTilingProgress() {
		return this.tilingProgress;
	}

	getIsAssetUploaded() {
		return this.isAssetUploaded;
	}

	getAssetId() {
		return this.assetId;
	}

	getAssetIdNum() {
		const id = Number.parseInt(this.assetId, 10);
		return Number.isFinite(id) ? id : undefined;
	}

	get asset_id() {
		return this.assetId;
	}

	getId() {
		return this.assetId;
	}

	getTransparency() {
		return this.transparency;
	}

	setTransparency(value) {
		this.transparency = value;
	}

	getIsVisible() {
		return this.isVisible;
	}

	getVisibility() {
		return this.isVisible;
	}

	setIsVisible(value) {
		this.isVisible = value;
	}

	getTimeStamp() {
		const date = new Date(`${this.selectedDate}T${this.selectedTime}`);
		return this.selectedDate && this.selectedTime
			? [
					date.toLocaleString("en-US", { day: "2-digit" }),
					`${date.toLocaleString("en-US", { month: "short" })},`,
					date.toLocaleString("en-US", { year: "numeric" }),
					date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit" }),
				].join(" ")
			: "";
	}

	getExpansion() {
		return this.expansion;
	}

	setExpansion(value) {
		this.expansion = value;
	}

	getIsCreating() {
		return this.is_creating;
	}

	setIsCreating(isCreating) {
		this.is_creating = isCreating;
	}

	getIsLoaded() {
		return this.isLoaded;
	}

	setIsLoaded(isLoaded) {
		this.isLoaded = isLoaded;
	}

	setImage(image) {
		this.file = image;
	}
	setLatLon(lat, lon) {
		this.latitude = lat;
		this.longitude = lon;
	}
	setImageDimension(width, height) {
		this.imageWidth = width;
		this.imageHeight = height;
	}
	setIsDraped(isDraped) {
		this.isDraped = isDraped;
	}
	setElevation(elevation) {
		this.elevation = elevation;
	}
	getElevation() {
		return this.elevation;
	}
	setHasImage(hasImage) {
		this.hasImage = hasImage;
	}
	getHasImage() {
		return this.hasImage;
	}
}
